import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getStatusLabel } from '../../../utils/appointmentUtils';
import {
  AppointmentPaymentPill,
  AppointmentStatusPill,
} from '../../../components/overview/AppointmentStatusPill';
import { getCompanyAppointmentUrlSegment } from '../../../shared/booking/appointments/companyAppointmentRouting';
import { listAppointments } from '../../../api/appointments';
import { PageSkeleton } from '../../../components/loading/skeletons';
import './ClientAppointments.css';

const APPOINTMENTS_PER_PAGE = 10;

const SORT_OPTIONS = [
  { value: 'date-desc', label: 'Date (newest)' },
  { value: 'date-asc', label: 'Date (oldest)' },
  { value: 'service-asc', label: 'Service (A–Z)' },
  { value: 'service-desc', label: 'Service (Z–A)' },
  { value: 'staff-asc', label: 'Staff (A–Z)' },
  { value: 'staff-desc', label: 'Staff (Z–A)' },
  { value: 'status-asc', label: 'Status (A–Z)' },
  { value: 'status-desc', label: 'Status (Z–A)' },
];

const compareText = (a, b) =>
  String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });

const sortAppointments = (list, sortBy) => {
  const sorted = [...list];
  switch (sortBy) {
    case 'date-asc':
      return sorted.sort((a, b) => a.startTime - b.startTime);
    case 'service-asc':
      return sorted.sort((a, b) => compareText(a.serviceName, b.serviceName));
    case 'service-desc':
      return sorted.sort((a, b) => compareText(b.serviceName, a.serviceName));
    case 'staff-asc':
      return sorted.sort((a, b) => compareText(a.employeeName, b.employeeName));
    case 'staff-desc':
      return sorted.sort((a, b) => compareText(b.employeeName, a.employeeName));
    case 'status-asc':
      return sorted.sort((a, b) => compareText(getStatusLabel(a), getStatusLabel(b)));
    case 'status-desc':
      return sorted.sort((a, b) => compareText(getStatusLabel(b), getStatusLabel(a)));
    case 'date-desc':
    default:
      return sorted.sort((a, b) => b.startTime - a.startTime);
  }
};

const CalendarEmptyIcon = () => (
  <svg className="clientApptList__emptyIcon" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z" />
  </svg>
);

const parseAppointmentStartTime = (data) => {
  if (!data?.dateTime) return null;
  try {
    if (typeof data.dateTime?.toDate === 'function') return data.dateTime.toDate();
  } catch {
    // fall through
  }
  if (data.dateTime instanceof Date) return Number.isNaN(data.dateTime.getTime()) ? null : data.dateTime;
  const parsed = new Date(data.dateTime);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const mapAppointment = (appointment) => {
  const startTime = parseAppointmentStartTime(appointment);
  return {
    ...appointment,
    startTime,
    serviceName: appointment.serviceName || 'Appointment',
    status: appointment.status || (appointment.archived ? 'archived' : appointment.status),
    isArchived: Boolean(appointment.archived),
  };
};

const ClientAppointments = ({ client }) => {
  const { companyData } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('date-desc');

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!companyData?.id || !client?.id) return;

      try {
        setLoading(true);
        setError(null);

        const result = await listAppointments({
          companyId: companyData.id,
          clientId: client.id,
          archived: 'all',
        });
        const allAppointments = (result.appointments || [])
          .map(mapAppointment)
          .filter((apt) => apt.startTime instanceof Date && !Number.isNaN(apt.startTime.getTime()));

        setAppointments(allAppointments);
      } catch (err) {
        console.error('[ClientAppointments] fetch failed', err);
        setError('Failed to load appointments');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [companyData?.id, client?.id]);

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const sortedAppointments = useMemo(
    () => sortAppointments(appointments, sortBy),
    [appointments, sortBy]
  );

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    const appointmentsSection = document.getElementById('appointments-section');
    if (appointmentsSection) {
      appointmentsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleAppointmentClick = (appointment) => {
    if (!appointment?.id) return;

    const appointmentSegment = getCompanyAppointmentUrlSegment(appointment);
    if (!appointmentSegment) return;

    if (appointment.isArchived) {
      navigate(`/app/overview/appointment/${appointmentSegment}?archived=1`, {
        state: { fromArchive: true },
      });
      return;
    }

    navigate(`/app/overview/appointment/${appointmentSegment}`, {
      state: { appointment },
    });
  };

  const handleRowKeyDown = (event, appointment) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAppointmentClick(appointment);
    }
  };

  if (loading) {
    return <PageSkeleton showHeader={false} rows={6} label="Loading appointments" />;
  }

  if (error) {
    return <p className="clientApptList__error" role="alert">{error}</p>;
  }

  if (!appointments.length) {
    return (
      <div className="clientApptList__empty">
        <CalendarEmptyIcon />
        <h3 className="clientApptList__emptyTitle">No appointments</h3>
        <p className="clientApptList__emptyText">This client hasn&apos;t booked any appointments yet</p>
      </div>
    );
  }

  const totalPages = Math.ceil(sortedAppointments.length / APPOINTMENTS_PER_PAGE);
  const startIndex = (page - 1) * APPOINTMENTS_PER_PAGE;
  const currentAppointments = sortedAppointments.slice(startIndex, startIndex + APPOINTMENTS_PER_PAGE);

  return (
    <div className="clientApptList" id="appointments-section">
      <div className="app-table-wrap clientApptList__tableWrap">
        <table className="app-data-table clientApptList__table">
          <thead>
            <tr>
              <th className="clientApptList__colDate" scope="col">Date</th>
              <th className="clientApptList__colService" scope="col">Service</th>
              <th className="clientApptList__colStaff" scope="col">Staff</th>
              <th className="clientApptList__colStatus" scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {currentAppointments.map((appointment) => (
                <tr
                  key={`${appointment.id}-${appointment.isArchived ? 'archived' : 'active'}`}
                  className="clientApptList__row is-clickable"
                  onClick={() => handleAppointmentClick(appointment)}
                  onKeyDown={(event) => handleRowKeyDown(event, appointment)}
                  tabIndex={0}
                  role="button"
                  aria-label={`View ${appointment.serviceName || 'appointment'} on ${formatDate(appointment.startTime)}`}
                >
                  <td className="clientApptList__date">{formatDate(appointment.startTime)}</td>
                  <td className="clientApptList__service">
                    {appointment.serviceName || 'Unknown Service'}
                  </td>
                  <td className="clientApptList__staff">
                    {appointment.employeeName || 'Not assigned'}
                  </td>
                  <td className="clientApptList__statusCell">
                    <span className="clientApptList__badges">
                      <AppointmentStatusPill appointment={appointment} />
                      <AppointmentPaymentPill appointment={appointment} />
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div className="clientApptList__footer">
          <p className="clientApptList__count">
            Showing {startIndex + 1}-{Math.min(startIndex + APPOINTMENTS_PER_PAGE, sortedAppointments.length)} of {sortedAppointments.length}
          </p>
          <div className="clientApptList__footerActions">
            <div className="clientApptList__sort">
              <label className="clientApptList__sortLabel" htmlFor="client-appt-sort">
                Sort by
              </label>
              <select
                id="client-appt-sort"
                className="clientApptList__sortSelect"
                value={sortBy}
                onChange={handleSortChange}
                aria-label="Sort appointments"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {totalPages > 1 && (
              <nav className="clientApptList__pagination" aria-label="Appointment pages">
            <button
              type="button"
              className="clientApptList__pageBtn"
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                className={`clientApptList__pageBtn${pageNum === page ? ' is-active' : ''}`}
                onClick={() => handlePageChange(pageNum)}
                aria-current={pageNum === page ? 'page' : undefined}
              >
                {pageNum}
              </button>
            ))}
            <button
              type="button"
              className="clientApptList__pageBtn"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              aria-label="Next page"
            >
              Next
            </button>
          </nav>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAppointments;
